import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import pick from 'lodash/pick';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import config from '../../config/config';
import { useGetPayerPlansQuery } from '../../redux/services/payerPlanApi';
import CustomInput from '../common/CustomInput';
import PayerPlanEditorButton from './PayerPlanEditorButton';

const getOptions = objects => (
  (objects || []).map(obj => ({
    label: obj?.payer?.name,
    value: obj.id,
  }))
);

const filterCaseBillingType = caseBillingType => plan => {
  if (!caseBillingType) return true;
  switch (caseBillingType) {
    case 'DIRECT_BILL':
      return ['AUTO', 'WORK_COMP', 'COMMERCIAL', 'LIEN', 'CORRECTIONS'].includes(plan.category);
    case 'COURTESY_BILL':
      return plan.category === 'COMMERCIAL';
    case 'CASH':
      return false;
    default:
      return true;
  }
};

const handleTextChange = ({setTextInput, setQuery, limit = 100}) => e => {
  const value = get(e, 'target.value', '');
  setTextInput(value);
  setQuery({name: value, limit});
};

const handleSubmit = ({debouncedTextChangeHandler, setPlanId, onSubmit}) => (e, val, reason) => {
  if (reason === 'selectOption') {
    e.target = {...(e.target || {}), value: val.value};
    if (val.value) {
      setPlanId(val.value);
      return onSubmit(e, {id: val.value});
    }
    if (val.id) {
      setPlanId(val.id);
      return onSubmit(e, {id: val.id});
    }
  }
  debouncedTextChangeHandler(e);
};

const PayerPlanSelect = props => {
  const {onSubmit, limit, patientId, isActive, planCategory} = props;
  const [textInput, setTextInput] = useState('');
  const [planId, setPlanId] = useState(props.defaultValue);
  const [query, setQuery] = useState({limit});
  let filter = {...query, patient_id: patientId, is_active: isActive};
  if (planCategory) filter = {...filter, plan_category: planCategory};
  const plansQuery = useGetPayerPlansQuery(filter, {skip: !patientId});
  const isLoading = plansQuery.isLoading;
  const textChangeHandler = handleTextChange({limit, setTextInput, setQuery});
  const debouncedTextChangeHandler = useCallback(
    debounce(textChangeHandler, 500),
    [textChangeHandler]
  );
  console.log('planCategory', props?.planCategory);
  const plans = (get(plansQuery, 'data.data') || [])
    .filter(filterCaseBillingType(props?.planCategory));
  const options = getOptions(plans);
  if (props.defaultValue && !options.find(o => o.value === props.defaultValue)) {
    return <></>;
  }
  const defaultOption = options.find(o => o.value === props.defaultValue);
  return (
    <Stack gap={.25}>
      <CustomInput {...props}>
        <Autocomplete
          type='text'
          style={{minWidth: props.minWidth || null, maxWidth: props.maxWidth || null}}
          options={options}
          disabled={!patientId}
          filterOptions={x => x}
          loading={isLoading}
          onChange={handleSubmit({debouncedTextChangeHandler, onSubmit, setPlanId})}
          defaultValue={defaultOption}
          renderInput={params => (
            <TextField
              placeholder={(props.patientId ? props.placeholder : 'Select a Patient First') || 'Select Plan'}
              onChange={debouncedTextChangeHandler}
              {...params}
              sx={props.sx}
              size={props.size}
              fullWidth={props.fullWidth}
              className={`${props.rounded && 'custom-text-field-rounded'}`}
              minWidth={props.minWidth}
              InputProps={{
                ...params.InputProps,
                autoFocus: props.autoFocus,
                startAdornment: <InputAdornment position="start">
                  <SearchIcon style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
                </InputAdornment>,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          {...pick(
            props, 
            'id', 'placeholder', 'freeSolo', 'disableClearable', 'autoFocus', 'onSubmit',
            'size',
            'fullWidth',
            'minWidth'
          )}
            />
      </CustomInput>
      <Stack direction='row' spacing={1}>
        {
          props?.allowCreate && props?.patientId && <PayerPlanEditorButton
            editing={false}
            patientId={props.patientId} 
            planCategory={props.planCategory}
            variant='text'
            size='small'
            buttonText='Create'
            afterSubmit={(e, {id}) => setPlanId(id)}
          />
        }
        {
          props?.allowEdit && props?.patientId && planId && <PayerPlanEditorButton
            editing={true}
            payerPlanId={planId}
            patientId={props.patientId} 
            planCategory={props.planCategory}
            variant='text'
            size='small'
            buttonText='Edit'
          />
        }
      </Stack>
    </Stack>
  );
};

PayerPlanSelect.defaultProps = {
  planCategory: null,
  isActive: true,
  allowCreate: false,
  autoFocus: false,
  defaultValue: null,
  disableClearable: false,
  freeSolo: true,
  id: 'PayerPlanSelect.Autocomplete',
  placeholder: 'Search Cases',
  onSubmit: (e, {id}) => console.log('PayerPlanSelect.onSubmit', id),
  size: 'small',
  minHeight: '28px',
  fullWidth: true,
  padding: 0.5,
  limit: 100,
};

PayerPlanSelect.propTypes = {
  patientId: PropTypes.number.isRequired,
  planCategory: PropTypes.string,
  isActive: PropTypes.bool,
  allowCreate: PropTypes.bool,
  autoFocus: PropTypes.bool,
  defaultValue: PropTypes.string,
  disableClearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  size: PropTypes.string,
  variant: PropTypes.string, // chip variant
};

export default PayerPlanSelect;